import { IconPencil, IconTag, IconTrash } from "@tabler/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "styled-components";

import { TableThWithSortIcon } from "@sso/components/TableThWithSortIcon";
import { Dialog } from "@sso/constant";
import { useAppDispatch } from "@sso/hooks/useRedux";
import { openDialog } from "@sso/store/slices/dialog";
import { setPricingSoftwareLicenceId } from "@sso/store/slices/pricing";
import { setSoftwareLicenceSortColumn } from "@sso/store/slices/softwareLicence";
import { LicenceKeys, LicenceType, SoftwareLicenceSummary, SoftwareLicenceSummaryKeys, TableColumn } from "@sso/types";
import { Flex } from "@sso/ui/FlexBox";
import { IconButton } from "@sso/ui/IconButton";
import { LayoutTable, TD, TH, TableWrapper } from "@sso/ui/Table";

import LicenceTypes from ".";

interface SoftwareLicencesTableProps {
  data: SoftwareLicenceSummary[];
  tableColumns: TableColumn<string>[];
  dataKeys?: any;
}

const LicenceTypesTable = ({ data, tableColumns, dataKeys }: SoftwareLicencesTableProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const handleSortAction = (column: string) => {
    dispatch(setSoftwareLicenceSortColumn(column));
  };

  const navigate = useNavigate();
  const { softwareName } = useParams();

  const handleOnRemoveClick = (id: string) => {
    dispatch(openDialog({ type: Dialog.DELETE_LICENCE_TYPE, id }));
  };

  const handleEditPricing = (sl: SoftwareLicenceSummary) => {
    dispatch(setPricingSoftwareLicenceId(sl.softwareLicenceId));
    navigate(`/management/licence-types/${softwareName}/${sl.name}/pricing`);
  };

  const editLicenceType = (sl: SoftwareLicenceSummary) => {
    const id = sl.softwareLicenceId;
    dispatch(openDialog({ type: Dialog.EDIT_LICENCE_TYPE, id }));
  };

  return (
    <TableWrapper>
      <LayoutTable>
        <thead>
          <tr>
            {tableColumns.map(col => (
              <TH key={col.field} sortable={col.sortable} onClick={() => col.sortable && handleSortAction(col.field)}>
                {col.sortable ? <TableThWithSortIcon column={col.field} title={col.name} /> : col.name}
              </TH>
            ))}
            <TH textAlign="center">Actions</TH>
          </tr>
        </thead>
        <tbody>
          {data.map(softwareLicence => (
            <tr key={softwareLicence.softwareLicenceId}>
              {tableColumns
                .map(x => x.field as SoftwareLicenceSummaryKeys)
                .map(key => (
                  <TD key={key}>{softwareLicence[key]}</TD>
                ))}
              <TD>
                <Flex>
                  {softwareLicence[SoftwareLicenceSummaryKeys.LICENCE_TYPE] !== LicenceType.TRIAL && (
                    <IconButton
                      color={theme.colors.grey[400]}
                      hoverColor={theme.colors.grey[700]}
                      onClick={() => handleEditPricing(softwareLicence)}
                      title="Edit pricing"
                    >
                      <IconTag />
                    </IconButton>
                  )}

                  <IconButton
                    color={theme.colors.grey[400]}
                    hoverColor={theme.colors.grey[700]}
                    onClick={() => editLicenceType(softwareLicence)}
                    title="Edit licence type"
                  >
                    <IconPencil />
                  </IconButton>

                  <IconButton
                    color={theme.colors.grey[700]}
                    hoverColor={theme.colors.error.main}
                    onClick={() => handleOnRemoveClick(softwareLicence.softwareLicenceId)}
                    title="Delete licence type"
                  >
                    <IconTrash />
                  </IconButton>
                </Flex>
              </TD>
            </tr>
          ))}
        </tbody>
      </LayoutTable>
    </TableWrapper>
  );
};

export default LicenceTypesTable;
